import { useProductPageStore } from '../../contexts/ProductPageStore'
import { validateDigitalAsset } from '../digitalAssets/digitalAssetUtils'
import DigitalAssets from '../digitalAssets/digitalAssets'
import styles from './details.module.scss'
import Prop65WarningIcon from './prop65WarningIcon'

import React, { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

import { useIntersectionObserver } from '@speedwaymotors/clutch/Hooks/useIntersectionObserver'
import ErrorBoundary from 'Clutch/UtilityComponents/ErrorBoundary'

import ColorFinder from 'Clutch/Utilities/ColorFinder'

import Accordion from 'Clutch/Molecules/Accordion'

import Card from 'Clutch/Atoms/Card'
import Divider from 'Clutch/Atoms/Divider'
import Html from 'Clutch/Atoms/Html'
import Svg from 'Clutch/Atoms/Svg'
import TextLink from 'Clutch/Atoms/TextLink'
import Typography from 'Clutch/Atoms/Typography'

// something on the context to make it okay to not being the loading state
// a component that will render a skeleton if flag to not be ready

const Details = ({}) => {
  const tabs = useProductPageStore((x) => x.tabs)?.filter(
    (tab) => tab && tab.key && tab.key.length && tab.value && tab.value.length && tab.key !== 'Installation Tips',
  )
  const digitalAssets = useProductPageStore((x) => x.digitalAssets)?.filter((digitalAsset) =>
    validateDigitalAsset(digitalAsset),
  )
  const isDetailsAccordionOpen = useProductPageStore((x) => x.isDetailsAccordionOpen)
  const isDocsReadyForScroll = useProductPageStore((x) => x.isDocsReadyForScroll)
  const manufacturerPartNumber = useProductPageStore((x) => x.manufacturerPartNumber)
  const brand = useProductPageStore((x) => x.brand)
  const rebates = useProductPageStore((x) => x.rebates)
  const isProp65 = useProductPageStore((x) => x.isProp65)
  const isCARB = useProductPageStore((x) => x.isCARB)
  const cARBSummary = useProductPageStore((x) => x.cARBSummary)
  const cARBDescription = useProductPageStore((x) => x.cARBDescription)
  const isCarbAccordionOpen = useProductPageStore((x) => x.isCarbAccordionOpen)
  const toggleAccordion = useProductPageStore((x) => x.toggleAccordion)
  const setIsReadyForScrollData = useProductPageStore((x) => x.setIsReadyForScrollData)

  const detailsClosedRef = useIntersectionObserver('detailsClosedInView')
  const detailsOpenRef = useIntersectionObserver('detailsOpenInView')
  const guidesDocsRef = useIntersectionObserver('guidesDocsInView')
  const carbProp65Ref = useIntersectionObserver('carbProp65InView')

  const linkStyling = ColorFinder('action', null, null)
  const headerStyling = ColorFinder(null, 'subtle', null)
  const warningBorderColor = ColorFinder(null, 'normal', null).color

  useEffect(() => {
    if (isDetailsAccordionOpen !== isDocsReadyForScroll) {
      setIsReadyForScrollData(isDetailsAccordionOpen, 'Details')
    }
  }, [isDetailsAccordionOpen])

  const [dummyRef, _] = useInView()

  if ((tabs || []).length === 0 && !isProp65 && !isCARB && (digitalAssets || []).length === 0) {
    return false
  }

  const detailsAccordionSummary = (
    <Typography as={'h2'} size={1.25} font={'heading'}>
      {manufacturerPartNumber && brand && brand.brandName
        ? `${brand.brandName} ${manufacturerPartNumber} Details`
        : 'Details'}
    </Typography>
  )

  const carbAccordionSummary = (
    <div>
      <Typography as={'h2'} size={1.25} font={'heading'}>
        CARB Warning
      </Typography>
      <Typography size={0.875} tone={'subtle'} inline>
        {cARBSummary}
      </Typography>
    </div>
  )

  const warrantyTab = Array.isArray(tabs) ? tabs.filter((x, i) => i > 0 && x?.key === 'Warranties') : []
  const filteredTabs = Array.isArray(tabs) ? tabs.filter((x, i) => i === 0 || x?.key !== 'Warranties') : []

  return (
    <Card fill dataTestId={'pdp_details'} ref={isDetailsAccordionOpen ? dummyRef : detailsClosedRef}>
      <Accordion
        intent={'action'}
        iconClosed={'plus'}
        iconOpened={'minus'}
        size={0.875}
        summary={detailsAccordionSummary}
        fill
        segmentAccordionName={'pdp details'}
        defaultOpen={isDetailsAccordionOpen}
        onChange={(isOpen) => toggleAccordion('Details', isOpen)}
      >
        <div ref={detailsOpenRef}>
          {filteredTabs?.map((tab, i) => (
            <ErrorBoundary key={`details_${tab.key}_${i}`}>
              {i != 0 ? (
                <Typography font={'bold'} size={1} data-testid={`details_${tab.key}`}>
                  {tab.key}
                </Typography>
              ) : (
                false
              )}
              <div>
                <Html html={tab.value} />
              </div>
            </ErrorBoundary>
          ))}
          {(rebates || []).length ? (
            <>
              <Typography
                font={'bold'}
                size={1}
                data-testid={`details_rebates`}
                id={'pdp_details_rebates'}
                className={styles.pdp_details_rebates}
              >
                Rebates
              </Typography>
              {rebates.map((rebate, i) => (
                <ErrorBoundary key={`details_rebates_${i}`}>
                  <Html html={rebate.rebateDescription} />
                </ErrorBoundary>
              ))}
            </>
          ) : (
            false
          )}
        </div>
        {digitalAssets?.length ? (
          <div ref={guidesDocsRef}>
            <Divider className={styles.guides_documents_divider} data-testid={`guides_docs_section`} />
            <Typography font={'bold'} id={'pdp_details_guides_docs'} className={styles.pdp_details_guides_docs}>
              {'Guides & Documents'}
            </Typography>
            <DigitalAssets digitalAssets={digitalAssets} />
          </div>
        ) : (
          false
        )}
        {warrantyTab.length > 0 && (
          <div>
            <Divider className={styles.california_laws_divider} data-testid={'warranty_divider'} />
            <Typography as={'h2'} size={1.25} font={'heading'}>
              Warranty Information
            </Typography>
            <Html html={warrantyTab[0]?.value} />
          </div>
        )}
        {isProp65 || isCARB ? (
          <div ref={carbProp65Ref}>
            <Divider className={styles.california_laws_divider} data-testid={'california_laws_divider'} />
            {cARBDescription ? (
              <div>
                <Accordion
                  iconClosed={'chevron-down'}
                  iconOpened={'chevron-up'}
                  size={0.875}
                  summary={carbAccordionSummary}
                  segmentAccordionName={'pdp carb'}
                  defaultOpen={isCarbAccordionOpen}
                  onChange={(isOpen) => toggleAccordion('Carb', isOpen)}
                >
                  <Html html={cARBDescription} />
                </Accordion>
                <Divider className={styles.california_laws_divider} data-testid={'california_laws_divider'} />
              </div>
            ) : (
              false
            )}

            {isProp65 ? (
              <div className={`${styles.california_laws_container} ${styles.prop65_container}`}>
                <div
                  className={styles.prop65_warning}
                  style={{ borderColor: warningBorderColor }}
                  data-testid={'details_prop65'}
                >
                  <TextLink
                    className={styles.prop65_card_link}
                    href={'/Info/prop65'}
                    aria-label={'Prop 65 information'}
                    data-testid={'details_prop65_info_link'}
                  />
                  <Prop65WarningIcon className={styles.prop65_warning_icon} />
                  <div>
                    <Typography size={0.875} font={'bold'}>
                      CA WARNING:
                    </Typography>
                    <Typography size={0.875}>
                      Can expose you to lead, a carcinogen and reproductive toxicant. See{' '}
                      <TextLink
                        className={styles.prop65_official_link}
                        size={0.875}
                        inline
                        underline
                        targetBlank
                        href={'https://www.p65warnings.ca.gov'}
                        data-testid={'details_prop65_official_link'}
                      >
                        www.P65Warnings.ca.gov
                      </TextLink>
                    </Typography>
                  </div>
                </div>
              </div>
            ) : (
              false
            )}
            {isCARB ? (
              <div className={styles.california_laws_container}>
                <Svg className={styles.california_laws_icon} icon={'CARB'} size={1} />
                <TextLink
                  size={0.875}
                  inline
                  underline
                  targetBlank
                  href={'https://www.speedwaymotors.com/Info/carb'}
                  data-testid={'details_CARB'}
                >
                  CARB warning
                </TextLink>
              </div>
            ) : (
              false
            )}
          </div>
        ) : (
          false
        )}
      </Accordion>
    </Card>
  )
}

function withLazyClientSkeleton(Component) {
  return (props) => {
    const isHydrated = useProductPageStore((x) => x.isLazyDataHydrated)

    if (!isHydrated) {
      return (
        <>
          <div>not so fancy skeleton</div>
        </>
      )
    }

    return (
      <>
        <Component {...props} />
      </>
    )
  }
}

export default Details
